import React, { useState } from 'react';
import {
  ExtensionSlot,
} from 'piral-core';

/**
 * Tabs Layout Component
 * 
 * This layout provides a tabbed interface where only one tab's content is visible at a time.
 * Users can switch between tabs by clicking tab headers.
 * Only configured tabs will be rendered (no empty tabs).
 * 
 * AVAILABLE EXTENSION SLOTS:
 * - Dynamically created: "tab-1", "tab-2", "tab-3", etc. (based on configured components)
 * 
 * TAB CONFIGURATION:
 * Customize tab titles by adding "title" or "tabTitle" to the component config:
 * ```json
 * "tab-1": {
 *   "id": "my-component",
 *   "instanceOf": "my-component",
 *   "instanceID": "my-component-1",
 *   "config": {
 *     "title": "General Settings"
 *   }
 * }
 * ```
 * If no title is specified, defaults to "Tab 1", "Tab 2", etc.
 * 
 * USE CASE:
 * - Settings panels with multiple sections
 * - Multi-step forms
 * - Organized content with different categories
 * - Wizard-style interfaces
 * 
 */
export default {
    Layout: (props) => {
      const { pageKey, components = [], ...otherProps } = props;
      const prefix = `${pageKey || ''}-`;
      
      // Determine which tabs exist based on components
      const tabNumbers = components.map((_, index) => index + 1);
      const [activeTab, setActiveTab] = useState(tabNumbers[0] || 1);
      
      // Get tab titles from component config or use defaults
      const getTabTitle = (index) => {
        const component = components[index - 1];
        return component?.config?.title || component?.config?.tabTitle || `Tab ${index}`;
      };
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%'
      }}>
        {/* Tab Headers */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          overflowX: 'auto'
        }}>
          {tabNumbers.map((tabNum) => (
            <button
              key={tabNum}
              onClick={() => setActiveTab(tabNum)}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: activeTab === tabNum ? '#ffffff' : 'transparent',
                borderBottom: activeTab === tabNum ? '2px solid #1976d2' : '2px solid transparent',
                marginBottom: '-2px',
                cursor: 'pointer',
                fontWeight: activeTab === tabNum ? '600' : '400',
                color: activeTab === tabNum ? '#1976d2' : '#666',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tabNum) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tabNum) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {getTabTitle(tabNum)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto',
          backgroundColor: '#ffffff'
        }}>
          {tabNumbers.map((tabNum) => (
            <div
              key={tabNum}
              style={{
                display: activeTab === tabNum ? 'block' : 'none'
              }}
            >
              <ExtensionSlot name={`${prefix}tab-${tabNum}`} params={otherProps} />
            </div>
          ))}
        </div>
      </div>
    );
  },
}
